import React, { useEffect, useMemo } from 'react';

import { useReportContext } from 'lib/context/ReportContext/ReportContext';
import { ReportContextProvider } from 'lib/context/ReportContext/ReportContextProvider';

import AGBasicTable from 'components/shared/AGBasicTable/AGBasicTable';
import CustomStatusBarAggregationComponent from 'components/shared/AGTable/CustomStatusBarAggregationComponent';

import ReportDetails from './ReportDetails';
import ReportError from './ReportError';
import ReportHeader from './ReportHeader';
import { getDetailsCellRender } from './lib/getDetailsCellRender';
import { AUTO_SIZE_STRATEGY, DEFAULT_COLUMN_DEFS, checkForNestedChildren } from './lib/helpers';

import './ReportView.scss';

const ReportView = () => {
  return (
    <ReportContextProvider>
      <ReportContent />
    </ReportContextProvider>
  );
};

const ReportContent = () => {
  const { report, gridRef, columnDefs, isLoading } = useReportContext();

  const rowData = useMemo(() => report?.result, [report]);

  const statusPanelData = report?.statusPanelData;

  const defaultColDef = useMemo(() => DEFAULT_COLUMN_DEFS, []);

  const autoSizeStrategy = useMemo(() => AUTO_SIZE_STRATEGY, []);

  const detailCellRendererParams = useMemo(() => {
    const child = checkForNestedChildren(report?.result)?.children;

    return getDetailsCellRender(child);
  }, [report]);

  useEffect(() => {
    if (!gridRef.current?.api) return;

    if (isLoading) {
      gridRef.current?.api?.showLoadingOverlay();
    } else {
      gridRef.current?.api?.hideOverlay();
    }
  }, [isLoading, gridRef]);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        ...(statusPanelData
          ? [
              {
                statusPanel: CustomStatusBarAggregationComponent,
                statusPanelParams: {
                  statusPanelData
                },
                align: 'center'
              }
            ]
          : [])
      ]
    };
  }, [statusPanelData]);

  if (!report && !isLoading) return <ReportError />;

  return (
    <>
      <ReportHeader />
      <ReportDetails />
      <AGBasicTable
        key={!!rowData}
        rowData={rowData}
        columnDefs={columnDefs}
        customClassName="report-view-container"
        gridRef={gridRef}
        defaultColDef={defaultColDef}
        autoSizeStrategy={autoSizeStrategy}
        masterDetail
        detailCellRendererParams={detailCellRendererParams}
        detailRowAutoHeight
        statusBar={statusBar}
        noRowsOverlayComponent={() => (
          <div className="flex flex-col justify-center ">
            <h6 className="text-primary-900">No rows to show!</h6>
          </div>
        )}
      />
    </>
  );
};

export default ReportView;
